import { methodFieldId } from '@/components/admin/reglages/method-contracts';
import { type MethodFieldRef, isRequired, maxOf, problemOf } from '@/lib/method-draft';
import {
  METHOD_FUNCTION_FIELD_LABEL,
  METHOD_OPTIONAL_MARK,
  METHOD_STYLE_FIELD_LABEL,
  METHOD_TOUCHED_MARK,
  METHOD_TYPICAL_WORDS_PLACEHOLDER,
  characterCount,
  fieldRefusal,
} from '@/lib/method-labels';
import { cn } from '@/lib/utils';
import { TextArea, TextField } from '@pms-digital/design-system';
import { useId } from 'react';

/**
 * One field of a card of Réglages > Méthode (analyste.md §4.2, maquette
 * `Methode`): its label, the tag that says it moved, the control, the counter
 * where the planche draws one, and the refusal under it.
 *
 * ## The bound is a CAP, and the refusal stands behind it
 *
 * `maxLength` cuts the typing and the pasting at the bound - the régime of the
 * direction of a criterion and of a persona, the other editors whose draft is
 * autosaved - and `withFunctionField` / `withStyleField` cut whatever comes in
 * otherwise. It is not a courtesy: the draft schema keeps the bounds, so a draft
 * that held a value past one would be refused by EVERY autosave, and the
 * retouches of the other fields would never reach the server (`method-draft.ts`).
 * The refusal under the field (« 41 caractères : 40 au plus ») is the mirror of
 * the schema for a value that reaches the field some other way, measured the way
 * the schema measures - the blanks of the edges are not counted - so the counter,
 * the refusal and the server agree; typing cannot reach it.
 *
 * ## The tag says what moved
 *
 * A field that differs from the development version wears its tag « modifié » - the same
 * signal as the head's « Brouillon modifié ». The planche also paints it yellow
 * (`jaune-deep` border on `jaune-tint`), which the package `TextField` / `TextArea` cannot
 * take (no tinted state, no `className`): the tag is the signal now, beside the label, and
 * `data-touched` stays on the control for the suites.
 *
 * ## The package fields, and what they describe
 *
 * The refusal is the field's `message` (`status="error"`), so the field links it and says
 * itself invalid; the counter is drawn by this file under the field, as before. The package
 * field sets its own `aria-describedby` (its message), so the tag and the counter are no
 * longer part of the field's description.
 *
 * ## Reading is `readonly`, never `disabled`
 *
 * The régime of the criterion: a disabled control leaves the tab order and its
 * text stops being selectable, and the screen made for READING a version - or a
 * vitrine - would be the one place where a list of words cannot be copied.
 */

export interface MethodFieldProps {
  readonly fieldRef: MethodFieldRef;
  readonly value: string;
  /** The text differs from the development version's - never while a version is only being read. */
  readonly touched: boolean;
  /** A version opened by « Voir », or a vitrine: nothing is typable. */
  readonly readOnly: boolean;
  readonly onChange: (text: string) => void;
}

/** The rows of each area: with the padding of the planche they give its heights (100, 214, 100, 81). */
const AREA_ROWS: Readonly<Record<string, number>> = {
  designates: 4,
  words: 10,
  recognizes: 4,
  typicalWords: 3,
};

export function MethodField({ fieldRef, value, touched, readOnly, onChange }: MethodFieldProps) {
  const id = useId();
  const mark = methodFieldId(fieldRef);
  const max = maxOf(fieldRef);
  const problem = readOnly ? null : problemOf(value, isRequired(fieldRef), max);
  const label =
    fieldRef.part === 'functions'
      ? METHOD_FUNCTION_FIELD_LABEL[fieldRef.field]
      : METHOD_STYLE_FIELD_LABEL[fieldRef.field];
  const optional = fieldRef.part === 'styles' && fieldRef.field === 'typicalWords';
  /** The planche draws a counter under the long words of a function, and under nothing else. */
  const counted = fieldRef.part === 'functions' && fieldRef.field === 'words';
  const line = fieldRef.part === 'styles' && fieldRef.field === 'name';

  /** What the two kinds of control share: everything but their shape. */
  const shared = {
    id,
    label: (
      <>
        {label}
        {optional ? (
          <>
            {' '}
            <span className="font-normal">{METHOD_OPTIONAL_MARK}</span>
          </>
        ) : null}
      </>
    ),
    value,
    readOnly,
    maxLength: max,
    'data-testid': mark,
    'data-touched': touched ? 'true' : 'false',
    ...(problem === null
      ? {}
      : {
          status: 'error' as const,
          message: (
            <span id={`${id}-refus`} role="alert" data-testid={`${mark}-refus`}>
              {fieldRefusal(problem, value, max)}
            </span>
          ),
        }),
  };

  return (
    <div className="relative flex min-w-0 flex-col gap-1">
      {/* The tag sits on the label's line, at its right end, outside the label: inside, it
          would join the field's accessible name. */}
      {touched ? (
        <span
          id={`${id}-etat`}
          data-testid={`${mark}-modifie`}
          className="absolute top-0 right-0 inline-flex shrink-0 items-center rounded-full border border-jaune bg-jaune-tint px-2 font-semibold text-jaune-deep text-micro leading-4.25"
        >
          {METHOD_TOUCHED_MARK}
        </span>
      ) : null}

      {line ? (
        <TextField
          labelStyle="caps"
          {...shared}
          compact
          type="text"
          autoComplete="off"
          onChange={(event) => {
            if (!readOnly) onChange(event.currentTarget.value);
          }}
        />
      ) : (
        <TextArea
          labelStyle="caps"
          {...shared}
          rows={AREA_ROWS[fieldRef.field] ?? 4}
          {...(optional ? { placeholder: METHOD_TYPICAL_WORDS_PLACEHOLDER } : {})}
          onChange={(event) => {
            if (!readOnly) onChange(event.currentTarget.value);
          }}
        />
      )}

      {counted ? (
        <p
          id={`${id}-compte`}
          data-testid={`${mark}-compteur`}
          className={cn(
            'self-end text-caption tabular-nums leading-4',
            problem === 'too-long' ? 'font-semibold text-rouge-deep' : 'text-gray-600',
          )}
        >
          {characterCount(value, max)}
        </p>
      ) : null}
    </div>
  );
}
